import { Controller } from "@hotwired/stimulus"

function loadMapLibreScript(src) {
  if (!src) return Promise.reject(new Error("MapLibre GL SDK URL is empty or undefined"))
  if (window.maplibregl) return Promise.resolve(window.maplibregl)
  return new Promise((resolve, reject) => {
    let script = document.querySelector('script[data-maplibre]')
    if (!script) {
      script = document.createElement('script')
      script.setAttribute('data-maplibre', '')
      script.src = src
      document.head.appendChild(script)
    }
    if (window.maplibregl) { resolve(window.maplibregl); return }
    script.addEventListener('load', () => resolve(window.maplibregl), { once: true })
    script.addEventListener('error', reject, { once: true })
  })
}

export default class extends Controller {
  static targets = [ "mapContainer", "doctorCard" ]
  static values = { maplibreUrl: String }

  async connect() {
    this.markersMap = new Map()

    this.themeObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class' && this.map) {
          this.updateTheme()
        }
      })
    })
    this.themeObserver.observe(document.documentElement, { attributes: true })

    try {
      await loadMapLibreScript(this.maplibreUrlValue)
      this.initMap()
    } catch (error) {
      console.error("Failed to load MapLibre GL SDK:", error)
    }
  }

  disconnect() {
    if (this.themeObserver) {
      this.themeObserver.disconnect()
    }
    if (this.map) {
      this.map.remove()
    }
  }

  updateTheme() {
    if (!this.map) return
    const isDark = document.documentElement.classList.contains('dark') || document.body.classList.contains('dark')
    const tileUrl = isDark
      ? "https://basemaps.cartocdn.com/rastertiles/dark_all/{z}/{x}/{y}{r}.png"
      : "https://basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}{r}.png"

    try {
      const source = this.map.getSource('raster-tiles')
      if (source && typeof source.setTiles === 'function') {
        source.setTiles([tileUrl])
      }
    } catch (err) {
      console.warn("Failed to dynamically set tiles during theme switch:", err)
    }
  }

  initMap() {
    const defaultLat = 20.5937
    const defaultLng = 78.9629

    let elementId = this.mapContainerTarget.id
    if (!elementId) {
      elementId = "maplibre-map-" + Math.random().toString(36).substring(2, 9)
      this.mapContainerTarget.id = elementId
    }

    const isDark = document.documentElement.classList.contains('dark') || document.body.classList.contains('dark')
    const tileUrl = isDark
      ? "https://basemaps.cartocdn.com/rastertiles/dark_all/{z}/{x}/{y}{r}.png"
      : "https://basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}{r}.png"

    try {
      this.map = new window.maplibregl.Map({
        container: elementId,
        style: {
          version: 8,
          sources: {
            "raster-tiles": {
              type: "raster",
              tiles: [tileUrl],
              tileSize: 256,
              attribution: ''
            }
          },
          layers: [{
            id: "simple-tiles",
            type: "raster",
            source: "raster-tiles",
            minzoom: 0,
            maxzoom: 20
          }]
        },
        center: [defaultLng, defaultLat],
        zoom: 12,
        interactive: false,
        attributionControl: false
      })

      if (this.map.dragPan) this.map.dragPan.disable()
      if (this.map.scrollZoom) this.map.scrollZoom.disable()
      if (this.map.doubleClickZoom) this.map.doubleClickZoom.disable()
      if (this.map.boxZoom) this.map.boxZoom.disable()
      if (this.map.dragRotate) this.map.dragRotate.disable()
      if (this.map.keyboard) this.map.keyboard.disable()
      if (this.map.touchZoomRotate) this.map.touchZoomRotate.disable()
    } catch (err) {
      console.error("Failed to initialize MapLibre GL Map:", err)
      return
    }

    setTimeout(() => {
      if (this.map && typeof this.map.resize === 'function') {
        this.map.resize()
      }
    }, 150)

    this.doctorCardTargets.forEach(card => {
      this.addMarkerForCard(card)
    })

    this.fitBounds()
  }

  doctorCardTargetConnected(element) {
    if (this.map) {
      this.addMarkerForCard(element)
      this.fitBounds()
    }
  }

  doctorCardTargetDisconnected(element) {
    const id = element.dataset.mapDoctorId
    if (id && this.markersMap.has(id)) {
      const data = this.markersMap.get(id)
      if (data.marker) {
        data.marker.remove()
      }
      this.markersMap.delete(id)
      this.fitBounds()
    }
  }

  addMarkerForCard(element) {
    const id = element.dataset.mapDoctorId
    const latVal = element.dataset.mapDoctorLat
    const lngVal = element.dataset.mapDoctorLng

    if (!id || !latVal || !lngVal || latVal.trim() === "" || lngVal.trim() === "") return

    const lat = parseFloat(latVal)
    const lng = parseFloat(lngVal)

    if (isNaN(lat) || isNaN(lng)) return

    if (this.markersMap.has(id)) return

    const ml = window.maplibregl
    const name = element.dataset.mapDoctorName || "Doctor"
    const specialty = element.dataset.mapDoctorSpecialty || ""
    const hospital = element.dataset.mapDoctorHospital || ""
    const photo = element.dataset.mapDoctorPhoto
    const url = element.dataset.mapDoctorUrl || "#"
    const fee = element.dataset.mapDoctorFee
    // Cards may override the stat label (e.g. hospitals show "Est. Year" here,
    // not a consultation fee). Default to the consultation-fee label.
    const feeLabel = element.dataset.mapDoctorFeeLabel || (window.MediFyrI18n?.t?.compare_fee || "Consultation Fee")
    const rating = element.dataset.mapDoctorRating

    const popupHtml = `
      <div class="p-1 min-w-[200px] text-left">
        <div class="flex gap-2.5 items-start mb-2">
          ${photo ? `<img src="${photo}" class="w-10 h-10 rounded-lg object-cover bg-slate-50 border border-slate-200/50" />` : ''}
          <div>
            <h4 class="font-bold text-xs text-slate-900 dark:text-slate-100 leading-tight mb-0.5">
              <a href="${url}" class="hover:text-[#1F5FFF] transition-colors">${name}</a>
            </h4>
            <p class="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-0.5">${specialty}</p>
            ${rating ? `<div class="flex items-center gap-0.5 text-[10px] font-bold text-amber-500">★ ${rating}</div>` : ''}
          </div>
        </div>
        <div class="border-t border-slate-100 dark:border-slate-800/60 pt-1.5 mt-1.5 space-y-1">
          <div class="flex items-center gap-1.5 text-[10px] text-slate-600 dark:text-slate-400">
            <svg class="h-3 w-3 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>
            <span class="truncate font-semibold text-slate-700 dark:text-slate-300">${hospital}</span>
          </div>
          ${fee ? `
          <div class="flex items-center justify-between text-[10px] font-bold text-slate-800 dark:text-slate-200 pt-1">
            <span>${feeLabel}:</span>
            <span class="text-[#1F5FFF]">${fee}</span>
          </div>` : ''}
        </div>
      </div>
    `

    const htmlStr = `
      <div id="marker-pin-${id}" class="marker-pin-wrapper flex items-center justify-center w-8 h-8 rounded-full bg-[#1F5FFF] border-2 border-white shadow-md text-white font-bold text-xs transition-all duration-200 origin-center cursor-pointer">
        <svg class="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
        </svg>
      </div>
    `

    const markerContainer = document.createElement('div')
    markerContainer.className = "custom-maplibre-marker-container"
    markerContainer.style.width = "32px"
    markerContainer.style.height = "32px"
    markerContainer.style.display = "flex"
    markerContainer.style.alignItems = "center"
    markerContainer.style.justifyContent = "center"
    markerContainer.innerHTML = htmlStr.trim()

    const pinElement = markerContainer.firstChild

    const popup = new ml.Popup({
      closeButton: false,
      offset: 25,
      autoPan: false
    })
      .setLngLat([lng, lat])
      .setHTML(popupHtml)

    const marker = new ml.Marker({
      element: markerContainer
    })
      .setLngLat([lng, lat])
      .setPopup(popup)
      .addTo(this.map)

    pinElement.addEventListener('click', () => {
      this.selectCard(id, element)
    })

    pinElement.addEventListener('mouseenter', () => this.highlightMarker(id))
    pinElement.addEventListener('mouseleave', () => this.clearMarkerHighlight(id))

    this.markersMap.set(id, { marker, popup, lat, lng })

    element.addEventListener('mouseenter', () => this.highlightMarker(id))
    element.addEventListener('mouseleave', () => this.clearMarkerHighlight(id))
  }

  fitBounds() {
    if (this.markersMap.size === 0) return

    let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180
    this.markersMap.forEach((data) => {
      const lat = data.lat
      const lng = data.lng
      if (lat < minLat) minLat = lat
      if (lat > maxLat) maxLat = lat
      if (lng < minLng) minLng = lng
      if (lng > maxLng) maxLng = lng
    })

    if (minLat <= maxLat && minLng <= maxLng) {
      this.map.fitBounds([
        [minLng, minLat],
        [maxLng, maxLat]
      ], {
        padding: 50,
        maxZoom: 15
      })
    }
  }

  highlightMarker(id) {
    if (this.hasMapContainerTarget && this.mapContainerTarget.offsetWidth === 0) return

    const data = this.markersMap.get(id)
    if (data && data.popup) {
      data.popup.addTo(this.map)

      const pinElement = document.getElementById(`marker-pin-${id}`)
      if (pinElement) {
        pinElement.classList.remove('bg-[#1F5FFF]', 'border-white')
        pinElement.classList.add('bg-[#04CE78]', 'border-emerald-200', 'scale-125', 'ring-4', 'ring-[#04CE78]/30')
      }
    }
  }

  clearMarkerHighlight(id) {
    if (this.hasMapContainerTarget && this.mapContainerTarget.offsetWidth === 0) return

    const data = this.markersMap.get(id)
    if (data && data.popup) {
      data.popup.remove()

      const pinElement = document.getElementById(`marker-pin-${id}`)
      if (pinElement) {
        pinElement.classList.remove('bg-[#04CE78]', 'border-emerald-200', 'scale-125', 'ring-4', 'ring-[#04CE78]/30')
        pinElement.classList.add('bg-[#1F5FFF]', 'border-white')
      }
    }
  }

  selectCard(id, cardElement) {
    this.doctorCardTargets.forEach(card => {
      card.classList.remove('ring-4', 'ring-[#1F5FFF]/20', 'border-[#1F5FFF]')
    })

    cardElement.classList.add('ring-4', 'ring-[#1F5FFF]/20', 'border-[#1F5FFF]')

    cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}
